import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import { CONTROL_MARGIN } from "../../MuiThemes";
import { getHelperText, propertyHasErrors } from "./inputHelpers";
import { TimeCustomControlProps } from "../../Utils/Interfaces";
import dayjs from "dayjs";
import { DATETIME_FORMAT, TIME_FORMAT } from "../../sharedCode/metadata";
import { useTheme } from "@mui/material";
import { isValidTimeString } from "../../sharedCode/HelperMethods";

interface State {
    timeString: string;
    isValid: boolean;
}

export default function TimeEditorText(props: TimeCustomControlProps): JSX.Element {
    const theme = useTheme();
    const [state, setState] = useState<State>(getInitialState());
    return (
        <>
            <TextField
                id={props.sourceProperty}
                margin={CONTROL_MARGIN}
                fullWidth
                value={state.timeString}
                autoComplete="off"
                onChange={(e) => handleOnChange(e.target.value)}
                onBlur={handleBlur}
                onFocus={(event) => {
                    event.target.select();
                }}
                inputProps={{
                    "data-testid": `${props.sourceProperty}${props.sourceId}`,
                    inputMode: "numeric",
                    style: { textTransform: "uppercase", textAlign: "right", color: state.isValid ? theme.palette.text.primary : theme.palette.error.main },
                }}
                label={props.labelText}
                error={propertyHasErrors(props.errors, props.containerUrl, props.sourceId, props.sourceProperty)}
                helperText={getHelperText(props.errors, props.containerUrl, props.sourceId, props.sourceProperty)}
            />
        </>
    );

    function handleOnChange(value: string): void {
        if (!value) {
            setState({ timeString: "", isValid: false });
            return;
        }

        // sorry, didn't want them to have to type : all the time
        let newValue = value;
        if (value.length == 2 && state.timeString.length == 1) {
            newValue += ":";
        } else if (value.length == 3 && state.timeString.length == 2) {
            newValue = `${value.slice(0, 2)}:${value.slice(2)}`;
        } else if (value.length == 3 && state.timeString.length == 4) {
            newValue = value.slice(0, 2);
        } else if (newValue.length > 5) {
            return;
        }

        if (isValidTimeFragment(newValue)) {
            setState({ timeString: newValue, isValid: value.length == 5 });
        }
    }

    function handleBlur(): void {
        if (state.timeString !== props.initialValue) {
            if (state.timeString.length === 5) {
                // the format has already been validated by the regexps
                props.mergeChanges(props.sourceId, { [props.sourceProperty]: timeStringAsIsoDatetime() });
            } else {
                props.mergeChanges(props.sourceId, { [props.sourceProperty]: state.timeString });
            }
        }
    }

    function getInitialState(): State {
        const initialValue = props.initialValue ?? "";
        const timeString = isValidTimeString(initialValue) ? dayjs(initialValue).format(TIME_FORMAT) : initialValue;
        return { timeString, isValid: timeString.length === 5 && isValidTimeFragment(timeString) };
    }

    function timeStringAsIsoDatetime(): string {
        return dayjs(`${props.documentDate} ${state.timeString}`, DATETIME_FORMAT).toISOString();
    }

    function isValidTimeFragment(value: string): boolean {
        const regxs = [/[0-2]/, /([0-1][0-9]|2[0-3])/, /([0-1][0-9]|2[0-3]):/, /([0-1][0-9]|2[0-3]):([0-5])/, /([0-1][0-9]|2[0-3]):([0-5][0-9])/];

        const regExp = regxs[value.length - 1];
        const regExpMatchArray = value.match(regExp);

        return regExpMatchArray != null;
    }
}
